import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { CMSData } from './types';

export const defaultCMSData: CMSData = {
  hero: {
    welcomeText: 'WELCOME TO',
    headline: 'CAPELLA RESIDENCY',
    subheadline: 'Luxury Living in the Heart of Westlands',
    ctaPrimary: {
      label: 'Explore Residences',
      link: '/residences',
    },
    ctaSecondary: {
      label: 'Schedule a Tour',
      link: '/contact',
    },
    backgroundImage: 'https://readdy.ai/api/search-image?query=modern%20luxury%20residential%20building%20exterior%20with%20glass%20facades%20and%20contemporary%20architecture%20in%20urban%20setting%20with%20clear%20blue%20sky%20and%20landscaped%20entrance%20area%20showcasing%20premium%20real%20estate%20development&width=1920&height=1080&seq=hero-bg-001&orientation=landscape',
  },
  about: {
    sectionLabel: 'ABOUT US',
    headline: 'REDEFINING LUXURY LIVING',
    paragraph1: 'Capella Residency stands as a beacon of modern luxury in Westlands, offering an unparalleled living experience. Our meticulously designed apartments combine contemporary architecture with premium finishes, creating spaces that inspire and elevate everyday living.',
    paragraph2: 'With a prime location, world-class amenities, and flexible payment plans, we make luxury accessible. Each residence is crafted to provide comfort, style, and convenience, ensuring that every moment at Capella is extraordinary.',
    ctaPrimary: {
      label: 'View Residences',
      link: '/residences',
    },
    ctaSecondary: {
      label: 'Contact Us',
      link: '/contact',
    },
    sideImage: 'https://readdy.ai/api/search-image?query=elegant%20modern%20apartment%20interior%20with%20floor%20to%20ceiling%20windows%20natural%20light%20contemporary%20furniture%20and%20premium%20finishes%20showcasing%20luxury%20residential%20living%20space%20with%20city%20views&width=800&height=1000&seq=about-side-001&orientation=portrait',
  },
  apartmentsSection: {
    sectionLabel: 'OUR RESIDENCES',
    headline: 'CHOOSE YOUR PERFECT HOME',
    subheadline: 'Discover our collection of thoughtfully designed apartments, each offering a unique blend of luxury, comfort, and style.',
  },
  apartments: [
    {
      id: '1',
      title: '1 Bedroom Apartment',
      price: 'KSh 8.5M',
      size: '650 sq ft',
      image: 'https://readdy.ai/api/search-image?query=modern%20one%20bedroom%20apartment%20interior%20with%20open%20plan%20living%20area%20contemporary%20kitchen%20sleek%20furniture%20floor%20to%20ceiling%20windows%20and%20premium%20finishes%20in%20neutral%20tones%20showcasing%20compact%20luxury%20living%20space&width=800&height=600&seq=apt-1bed-001&orientation=landscape',
      specs: [
        'Open-plan living area',
        'Modern kitchen with premium appliances',
        'Spacious bedroom with built-in wardrobes',
        'Contemporary bathroom',
        'Private balcony',
      ],
      status: 'Available',
    },
    {
      id: '2',
      title: '2 Bedroom Apartment',
      price: 'KSh 12.5M',
      size: '950 sq ft',
      image: 'https://readdy.ai/api/search-image?query=spacious%20two%20bedroom%20apartment%20interior%20with%20elegant%20living%20room%20modern%20kitchen%20two%20bedrooms%20with%20ensuite%20bathrooms%20premium%20finishes%20and%20large%20windows%20with%20natural%20light%20in%20contemporary%20style&width=800&height=600&seq=apt-2bed-001&orientation=landscape',
      specs: [
        'Generous living and dining space',
        'Master bedroom with ensuite',
        'Second bedroom with built-in storage',
        'Guest bathroom',
        'Fully equipped kitchen',
        'Large balcony with views',
      ],
      status: 'Available',
    },
    {
      id: '3',
      title: '3 Bedroom Apartment',
      price: 'KSh 16.5M',
      size: '1,250 sq ft',
      image: 'https://readdy.ai/api/search-image?query=luxurious%20three%20bedroom%20apartment%20interior%20with%20spacious%20living%20area%20modern%20kitchen%20three%20bedrooms%20with%20ensuites%20premium%20finishes%20floor%20to%20ceiling%20windows%20and%20elegant%20contemporary%20design%20showcasing%20family%20luxury%20living&width=800&height=600&seq=apt-3bed-001&orientation=landscape',
      specs: [
        'Expansive living and dining area',
        'Master bedroom with walk-in closet and ensuite',
        'Two additional bedrooms with built-in wardrobes',
        'Two bathrooms plus powder room',
        'Premium kitchen with island',
        'Spacious balcony',
        'Study nook',
      ],
      status: 'Available',
    },
  ],
  gallerySection: {
    sectionLabel: 'GALLERY',
    headline: 'EXPERIENCE CAPELLA RESIDENCY',
    subheadline: 'Explore our stunning collection of images showcasing the beauty and elegance of Capella Residency.',
  },
  gallery: [
    {
      id: '1',
      url: 'https://readdy.ai/api/search-image?query=modern%20luxury%20residential%20building%20exterior%20with%20glass%20facades%20contemporary%20architecture%20landscaped%20entrance%20and%20premium%20finishes%20in%20daylight%20showcasing%20upscale%20real%20estate%20development&width=800&height=600&seq=gallery-ext-001&orientation=landscape',
      caption: 'Main Entrance',
      category: 'Exterior',
      altText: 'Capella Residency main entrance with modern architecture',
    },
    {
      id: '2',
      url: 'https://readdy.ai/api/search-image?query=elegant%20apartment%20living%20room%20interior%20with%20contemporary%20furniture%20floor%20to%20ceiling%20windows%20natural%20light%20premium%20finishes%20and%20modern%20decor%20in%20neutral%20tones&width=800&height=600&seq=gallery-int-001&orientation=landscape',
      caption: 'Living Room',
      category: 'Interior',
      altText: 'Spacious living room with modern furnishings',
    },
    {
      id: '3',
      url: 'https://readdy.ai/api/search-image?query=luxury%20residential%20swimming%20pool%20area%20with%20lounge%20chairs%20landscaping%20and%20modern%20building%20in%20background%20showcasing%20premium%20amenities%20in%20upscale%20apartment%20complex&width=800&height=600&seq=gallery-pool-001&orientation=landscape',
      caption: 'Swimming Pool',
      category: 'Amenities',
      altText: 'Resort-style swimming pool with lounge area',
    },
    {
      id: '4',
      url: 'https://readdy.ai/api/search-image?query=modern%20apartment%20bedroom%20interior%20with%20large%20bed%20premium%20bedding%20floor%20to%20ceiling%20windows%20city%20views%20contemporary%20furniture%20and%20elegant%20finishes%20in%20calming%20neutral%20tones&width=800&height=600&seq=gallery-bed-001&orientation=landscape',
      caption: 'Master Bedroom',
      category: 'Interior',
      altText: 'Luxurious master bedroom with city views',
    },
    {
      id: '5',
      url: 'https://readdy.ai/api/search-image?query=panoramic%20city%20skyline%20view%20from%20luxury%20apartment%20balcony%20during%20golden%20hour%20with%20modern%20urban%20landscape%20and%20clear%20sky%20showcasing%20premium%20location&width=800&height=600&seq=gallery-view-001&orientation=landscape',
      caption: 'City Views',
      category: 'Views',
      altText: 'Stunning panoramic views of Westlands',
    },
    {
      id: '6',
      url: 'https://readdy.ai/api/search-image?query=modern%20residential%20fitness%20center%20with%20exercise%20equipment%20mirrors%20and%20large%20windows%20with%20natural%20light%20showcasing%20premium%20gym%20amenities%20in%20luxury%20apartment%20building&width=800&height=600&seq=gallery-gym-001&orientation=landscape',
      caption: 'Fitness Center',
      category: 'Amenities',
      altText: 'State-of-the-art fitness center',
    },
  ],
  locationSection: {
    sectionLabel: 'LOCATION',
    headline: 'PRIME LOCATION IN WESTLANDS',
  },
  location: {
    address: {
      street: 'Muthithi Road',
      area: 'Westlands',
      city: 'Nairobi',
      country: 'Kenya',
    },
    landmarks: [
      { id: '1', name: 'Sarit Centre', distance: '5 min walk', icon: 'ri-shopping-bag-line' },
      { id: '2', name: 'Westgate Mall', distance: '8 min drive', icon: 'ri-store-line' },
      { id: '3', name: 'The Hub Karen', distance: '15 min drive', icon: 'ri-building-line' },
      { id: '4', name: 'Nairobi Hospital', distance: '10 min drive', icon: 'ri-hospital-line' },
      { id: '5', name: 'International Schools', distance: '5-10 min drive', icon: 'ri-school-line' },
      { id: '6', name: 'JKIA Airport', distance: '25 min drive', icon: 'ri-plane-line' },
    ],
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.8176449087!2d36.78844931475394!3d-1.2833693359876!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d6d3b3b3b3%3A0x1234567890abcdef!2sWestlands%2C%20Nairobi!5e0!3m2!1sen!2ske!4v1234567890123!5m2!1sen!2ske',
  },
  contactSection: {
    backgroundImage: 'https://readdy.ai/api/search-image?query=modern%20luxury%20residential%20building%20exterior%20at%20dusk%20with%20warm%20lighting%20glass%20facades%20and%20contemporary%20architecture%20showcasing%20premium%20real%20estate%20development%20in%20elegant%20urban%20setting&width=1920&height=800&seq=contact-bg-001&orientation=landscape',
    headline: 'READY TO FIND YOUR DREAM HOME?',
    subheadline: 'Contact us today to schedule a viewing or learn more about our flexible payment plans.',
    phone: '+254 796 886932',
    whatsapp: '+254796886932',
    ctaLabel: 'Get in Touch',
  },
  contact: {
    phone: '+254 796 886932',
    email: 'info@capellaresidency.com',
    officeAddress: 'Muthithi Road, Westlands, Nairobi, Kenya',
    officeHours: {
      'Monday - Friday': '9:00 AM - 6:00 PM',
      'Saturday': '10:00 AM - 4:00 PM',
      'Sunday': 'Closed',
    },
    socialMedia: {
      instagram: 'https://instagram.com/capellaresidency',
      facebook: 'https://facebook.com/capellaresidency',
      linkedin: 'https://linkedin.com/company/capellaresidency',
    },
    whatsapp: '+254796886932',
  },
  footer: {
    copyrightText: '© 2025 Capella Residency. All rights reserved.',
    developerName: 'Capella Developers Ltd',
    developerWhatsapp: '+254796886932',
  },
  seo: {
    home: {
      title: 'Capella Residency Westlands Nairobi | Luxury Apartments from KSh 8.5M',
      description: 'Discover luxury living at Capella Residency in Westlands, Nairobi. 1, 2 & 3 bedroom apartments from KSh 8.5M with flexible payment plans. Prime location near Sarit Centre. Schedule a viewing today.',
      keywords: ['luxury apartments Westlands', 'Capella Residency Nairobi', 'apartments for sale Westlands', 'real estate Nairobi', 'luxury living Kenya'],
      ogTitle: 'Capella Residency - Luxury Apartments in Westlands, Nairobi',
      ogDescription: 'Premium 1, 2 & 3 bedroom apartments in the heart of Westlands. Flexible payment plans available.',
      canonicalUrl: 'https://capellaresidency.com/',
    },
    about: {
      title: 'About Capella Residency — Luxury Apartments in Westlands Nairobi',
      description: 'Learn about Capella Residency, a premier luxury apartment development in Westlands Nairobi. Committed to redefining luxury living with world-class amenities, contemporary design, and flexible payment plans.',
      keywords: ['About Capella Residency', 'luxury apartment developer Nairobi', 'Westlands property development', 'premium apartments Nairobi', 'real estate company Kenya', 'Capella Residency Westlands', 'luxury living Nairobi'],
      ogTitle: 'About Capella Residency — Luxury Apartments in Westlands Nairobi',
      ogDescription: 'Discover Capella Residency — redefining luxury living in Westlands with premium apartments, world-class amenities, and exceptional service.',
      canonicalUrl: 'https://capellaresidency.com/about',
    },
    residences: {
      title: 'Our Residences | Studio, 1 & 2 Bedroom Luxury Apartments | Capella Residency',
      description: 'Explore luxury apartments for sale in Westlands, Nairobi. Studio apartments from KSh 5M, 1-bedroom apartments from KSh 7.5M, and 2-bedroom apartments from KSh 13M. Featuring premium finishes, modern architectural design, high-end amenities.',
      keywords: ['1 bedroom apartment Westlands', '2 bedroom apartment Nairobi', '3 bedroom luxury apartment', 'apartments for sale Kenya'],
      canonicalUrl: 'https://capellaresidency.com/residences',
    },
    gallery: {
      title: 'Gallery | Capella Residency Westlands | Luxury Apartment Photos',
      description: 'View stunning photos of Capella Residency. Explore our modern architecture, elegant interiors, premium amenities, and breathtaking views.',
      keywords: ['Capella Residency photos', 'luxury apartment gallery', 'Westlands apartments images'],
      canonicalUrl: 'https://capellaresidency.com/gallery',
    },
    location: {
      title: 'Location | Prime Westlands Address | Capella Residency Nairobi',
      description: 'Capella Residency is located on Muthithi Road in the heart of Westlands. 5 minutes walk to Sarit Centre, close to international schools, hospitals, and major business hubs.',
      keywords: ['Westlands location', 'Muthithi Road apartments', 'Nairobi prime location', 'Sarit Centre apartments'],
      canonicalUrl: 'https://capellaresidency.com/location',
    },
    contact: {
      title: 'Contact Us | Schedule a Viewing | Capella Residency Westlands',
      description: 'Get in touch with Capella Residency. Call +254 712 345 678 or visit our office on Muthithi Road, Westlands. Schedule a viewing or ask about payment plans.',
      keywords: ['contact Capella Residency', 'schedule apartment viewing Nairobi', 'Westlands real estate contact'],
      canonicalUrl: 'https://capellaresidency.com/contact-capella-residency',
    },
  },
  paymentPlans: [
    {
      id: '1',
      title: 'Cash Payment',
      discount: '10% Discount',
      description: 'Pay in full and enjoy the best price',
      features: [
        'Immediate 10% discount on total price',
        'Priority unit selection',
        'Fast-track processing',
        'No interest charges',
        'Immediate ownership transfer',
      ],
      whatsappMessage: 'Hi, I\'m interested in the Cash Payment plan for Capella Residency. Can you provide more details?',
    },
    {
      id: '2',
      title: '6 Months Plan',
      discount: '7% Discount',
      description: 'Flexible payment over 6 months',
      features: [
        '7% discount on total price',
        '30% deposit required',
        'Balance in 5 monthly installments',
        'No interest charges',
        'Ownership transfer upon completion',
      ],
      whatsappMessage: 'Hi, I\'m interested in the 6 Months Payment Plan for Capella Residency. Can you provide more details?',
    },
    {
      id: '3',
      title: '12 Months Plan',
      discount: '5% Discount',
      description: 'Spread payments over a year',
      features: [
        '5% discount on total price',
        '20% deposit required',
        'Balance in 11 monthly installments',
        'Low interest rate',
        'Ownership transfer upon completion',
      ],
      whatsappMessage: 'Hi, I\'m interested in the 12 Months Payment Plan for Capella Residency. Can you provide more details?',
    },
    {
      id: '4',
      title: '24 Months Plan',
      discount: 'Flexible Terms',
      description: 'Extended payment period for maximum flexibility',
      features: [
        'Extended 24-month payment period',
        '15% deposit required',
        'Balance in 23 monthly installments',
        'Competitive interest rate',
        'Ownership transfer upon completion',
      ],
      whatsappMessage: 'Hi, I\'m interested in the 24 Months Payment Plan for Capella Residency. Can you provide more details?',
    },
  ],
  branding: {
    logoUrl: 'https://static.readdy.ai/image/4e50e2e62c62c0ac856f5f26c28070fe/14928b36cd580b60de903725dd4a7e92.png',
    faviconUrl: 'https://static.readdy.ai/image/4e50e2e62c62c0ac856f5f26c28070fe/0d1f1f9523a17ed2d2650a1c8edb625a.jpeg',
    ogImageUrl: 'https://static.readdy.ai/image/4e50e2e62c62c0ac856f5f26c28070fe/0d1f1f9523a17ed2d2650a1c8edb625a.jpeg',
  },
  lastModified: new Date().toISOString(),
};

const CMS_ROW_KEY = 'capella_cms_v1';

/**
 * Deep merge: database values always win over defaults.
 * Only fills in keys that are completely missing from the DB row.
 */
function deepMerge<T extends object>(defaults: T, saved: Partial<T>): T {
  const result = { ...defaults } as T;
  for (const key in saved) {
    const savedVal = saved[key];
    const defaultVal = defaults[key];
    if (
      savedVal !== null &&
      savedVal !== undefined &&
      typeof savedVal === 'object' &&
      !Array.isArray(savedVal) &&
      typeof defaultVal === 'object' &&
      !Array.isArray(defaultVal)
    ) {
      result[key] = deepMerge(defaultVal as object, savedVal as object) as T[typeof key];
    } else if (savedVal !== undefined) {
      result[key] = savedVal as T[typeof key];
    }
  }
  return result;
}

/**
 * Load CMS data from Supabase.
 * If no row exists yet, seeds the database with defaultCMSData automatically.
 */
export const loadCMSData = async (): Promise<CMSData> => {
  if (!isSupabaseConfigured) {
    return defaultCMSData;
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const { data, error } = await supabase
      .from('cms_content')
      .select('data')
      .eq('id', CMS_ROW_KEY)
      .maybeSingle()
      .abortSignal(controller.signal);

    clearTimeout(timeout);

    if (error) {
      console.error('CMS load error:', error.message);
      return defaultCMSData;
    }

    // No row yet — seed the database with all default data
    if (!data) {
      console.info('No CMS data found in database. Seeding with defaults...');
      const seedPayload: CMSData = {
        ...defaultCMSData,
        lastModified: new Date().toISOString(),
      };
      const { error: seedError } = await supabase
        .from('cms_content')
        .upsert({ id: CMS_ROW_KEY, data: seedPayload }, { onConflict: 'id' });

      if (seedError) {
        console.error('Failed to seed CMS data:', seedError.message);
      } else {
        console.info('CMS data seeded successfully.');
      }
      return seedPayload;
    }

    return deepMerge(defaultCMSData, data.data as Partial<CMSData>);
  } catch (err) {
    console.error('CMS load exception:', err);
    return defaultCMSData;
  }
};

/**
 * Save CMS data to Supabase using upsert so it works
 * whether the row already exists or not.
 */
export const saveCMSData = async (cmsData: CMSData): Promise<void> => {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase is not connected. Please connect Supabase to save changes.');
  }

  const payload = {
    ...cmsData,
    lastModified: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('cms_content')
    .upsert(
      { id: CMS_ROW_KEY, data: payload },
      { onConflict: 'id' }
    );

  if (error) {
    throw new Error(`Failed to save CMS data: ${error.message}`);
  }
};
